import { Box, Button, Grid, Group, Image, Paper, Stack, Text, Textarea, TextInput, Tooltip } from '@mantine/core'
import { IconExternalLink, IconTrash } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'

import { AIInputWrapper } from '@/components/AIInputWrapper'
import { ALLOWED_IMAGE_TYPES, FileUpload } from '@/components/FileUpload'
import { InputLabel } from '@/components/InputLabel'
import { theme } from '@/configs/theme'
import { useAIFieldGeneration } from '@/hooks/useAIFieldGeneration'
import { useFileUpload } from '@/hooks/useFileUpload'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import * as classes from '@/styles/ContentModuleFormScreen.css'
import { getFieldDisplayName, getRequiredFieldDescriptionProps } from '@/utils/forms'

export const RequiredSettingsSection = observer(function RequiredSettingsSection(props: CMFormSection) {
  const { configStore } = useGlobalState()
  const { getFieldState, generateFieldByName, acceptCMSuggestion, rejectSuggestion, regenerateFieldByName } =
    useAIFieldGeneration(props)
  const [hasSteps, setHasSteps] = useState(false)

  // Track if we've already triggered auto-generation to avoid re-triggering
  const autoGenerationTriggeredRef = useRef(false)

  // Track whether the CM had steps when initially loaded (before any user action)
  const hadStepsOnLoadRef = useRef<boolean | null>(null)

  // Field refs for granular scrolling
  const fieldRefs = {
    name: useRef<HTMLInputElement>(null),
    shortDescription: useRef<HTMLTextAreaElement>(null),
    demographic: useRef<HTMLTextAreaElement>(null),
    introduction: useRef<HTMLTextAreaElement>(null),
  }

  // Expose field refs through props if available
  useEffect(() => {
    if (props.setFieldRefs) {
      props.setFieldRefs(fieldRefs as Record<string, React.RefObject<HTMLElement>>)
    }
  }, [fieldRefs, props.setFieldRefs])

  const { uploading: titleImageUploading, upload: uploadTitleImage } = useFileUpload({
    onPrepareError: () =>
      props.moduleForm.setFieldError('titleImage', 'Something went wrong. Please try again or contact support.'),
    onUploadError: () =>
      props.moduleForm.setFieldError('titleImage', 'Upload failed. Please try again or contact support.'),
    onUploadSuccess: () => props.moduleForm.setFieldError('titleImage', undefined),
  })

  useEffect(() => {
    setHasSteps(!!props.stepsQuery.data?.length)
  }, [props.stepsQuery.data])

  // Capture whether CM had steps on initial load (only set once per CM)
  useEffect(() => {
    if (hadStepsOnLoadRef.current === null && props.stepsQuery.isSuccess) {
      hadStepsOnLoadRef.current = (props.stepsQuery.data?.length ?? 0) > 0
    }
  }, [props.stepsQuery.isSuccess, props.stepsQuery.data?.length])

  // Reset tracking when taskId changes (different content module)
  useEffect(() => {
    autoGenerationTriggeredRef.current = false
    hadStepsOnLoadRef.current = null
  }, [props.taskId])

  // Auto-generate AI content for empty fields when steps become available
  // DISABLED: Auto-activation of AI generation has been disabled per user request
  // useEffect(() => {
  //   // Only trigger auto-generation if:
  //   // 1. We have steps
  //   // 2. We haven't already triggered auto-generation for this session
  //   // 3. The queries are successful (not loading)
  //   // 4. Triggered by: CM loaded with existing steps OR step saved via "Save & Exit"

  //   // CM had steps when initially loaded - safe to auto-generate immediately
  //   const hadStepsOnLoad = hadStepsOnLoadRef.current === true

  //   // Step just saved via "Save & Exit" button in StepModal
  //   const isStepJustSaved = (props.stepSavedTrigger ?? 0) > 0

  //   // If CM started with 0 steps, only trigger on explicit save signal
  //   // If CM started with steps, trigger immediately on load
  //   const shouldTrigger =
  //     props.stepsQuery.isSuccess && (hadStepsOnLoad || isStepJustSaved) && props.moduleForm.initialized

  //   if (hasSteps && !autoGenerationTriggeredRef.current && shouldTrigger) {
  //     autoGenerationTriggeredRef.current = true

  //     const formValues = props.moduleForm.getValues()

  //     // Check which fields are empty and trigger AI generation
  //     if (!formValues.name?.trim()) {
  //       generateFieldByName('cm', 'name')
  //     }

  //     if (!formValues.shortDescription?.trim()) {
  //       generateFieldByName('cm', 'shortDescription')
  //     }

  //     if (!formValues.demographic?.trim()) {
  //       generateFieldByName('cm', 'demographic')
  //     }

  //     if (!formValues.introduction?.trim()) {
  //       generateFieldByName('cm', 'introduction')
  //     }
  //   }
  // }, [
  //   hasSteps,
  //   props.stepsQuery.isSuccess,
  //   props.stepSavedTrigger,
  //   generateFieldByName,
  //   props.moduleForm,
  // ])

  return (
    <Grid gutter="xl">
      <Grid.Col span={12}>
        <AIInputWrapper
          disabled={!hasSteps}
          fieldState={getFieldState('name')}
          onAskSidekick={() => generateFieldByName('cm', 'name')}
          onAccept={() => acceptCMSuggestion('name')}
          onReject={() => rejectSuggestion('name')}
          onRegenerate={() => regenerateFieldByName('cm', 'name')}>
          <TextInput
            ref={fieldRefs.name}
            withAsterisk
            label={getFieldDisplayName('name')}
            disabled={!hasSteps}
            placeholder={!hasSteps ? 'Please add content above before editing this field' : undefined}
            {...props.moduleForm.getInputProps('name')}
            {...getRequiredFieldDescriptionProps(props.moduleForm, 'name', '')}
          />
        </AIInputWrapper>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6 }}>
        <AIInputWrapper
          disabled={!hasSteps}
          fieldState={getFieldState('shortDescription')}
          onAskSidekick={() => generateFieldByName('cm', 'shortDescription')}
          onAccept={() => acceptCMSuggestion('shortDescription')}
          onReject={() => rejectSuggestion('shortDescription')}
          onRegenerate={() => regenerateFieldByName('cm', 'shortDescription')}>
          <Textarea
            ref={fieldRefs.shortDescription}
            withAsterisk
            label={getFieldDisplayName('shortDescription')}
            autosize
            resize="vertical"
            disabled={!hasSteps}
            placeholder={!hasSteps ? 'Please add content above before editing this field' : undefined}
            classNames={{ input: theme.cx(classes.mskIntroductionInput) }}
            {...props.moduleForm.getInputProps('shortDescription')}
            {...getRequiredFieldDescriptionProps(
              props.moduleForm,
              'shortDescription',
              'This is what other Sidekicks will know about the module.',
            )}
          />
        </AIInputWrapper>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6 }}>
        <AIInputWrapper
          disabled={!hasSteps}
          fieldState={getFieldState('demographic')}
          onAskSidekick={() => generateFieldByName('cm', 'demographic')}
          onAccept={() => acceptCMSuggestion('demographic')}
          onReject={() => rejectSuggestion('demographic')}
          onRegenerate={() => regenerateFieldByName('cm', 'demographic')}>
          <Textarea
            ref={fieldRefs.demographic}
            withAsterisk
            label={getFieldDisplayName('demographic')}
            autosize
            resize="vertical"
            disabled={!hasSteps}
            placeholder={!hasSteps ? 'Please add content above before editing this field' : undefined}
            classNames={{ input: theme.cx(classes.mskIntroductionInput) }}
            {...props.moduleForm.getInputProps('demographic')}
            {...getRequiredFieldDescriptionProps(
              props.moduleForm,
              'demographic',
              'Who is the target audience for your content?',
            )}
          />
        </AIInputWrapper>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6 }}>
        {props.moduleForm.getValues().titleImage ? (
          <Box>
            <InputLabel label="Module Intro Image" />

            <Paper withBorder radius="md" p="sm" bg="gray.1">
              <Stack gap="sm">
                <Image
                  radius="md"
                  src={`${configStore.serverBaseUrl}/files/${props.moduleForm.getValues().titleImage}`}
                  h="auto"
                  w="100%"
                  style={{ cursor: 'pointer' }}
                  onClick={() =>
                    window.open(
                      `${configStore.serverBaseUrl}/files/${props.moduleForm.getValues().titleImage}`,
                      '_blank',
                    )
                  }
                />

                <Group gap="sm" justify="flex-end">
                  <Tooltip label="Open in new tab">
                    <Button
                      variant="subtle"
                      color="gray"
                      size="compact-sm"
                      rightSection={<IconExternalLink size={16} />}
                      onClick={() =>
                        window.open(
                          `${configStore.serverBaseUrl}/files/${props.moduleForm.getValues().titleImage}`,
                          '_blank',
                        )
                      }>
                      Preview
                    </Button>
                  </Tooltip>

                  <Tooltip label="Remove image">
                    <Button
                      variant="subtle"
                      color="red"
                      size="compact-sm"
                      rightSection={<IconTrash size={16} />}
                      onClick={() => props.moduleForm.setFieldValue('titleImage', '')}>
                      Remove
                    </Button>
                  </Tooltip>
                </Group>
              </Stack>
            </Paper>

            <Text c="dimmed" className={classes.inputDescription}>
              This image will be displayed when the module is first introduced.
            </Text>
          </Box>
        ) : (
          <FileUpload
            label="Module Intro Image"
            activateOnClick={false}
            loading={titleImageUploading}
            maxFiles={1}
            error={props.moduleForm.getInputProps('titleImage').error}
            titles={{ idle: 'Upload Image' }}
            accept={ALLOWED_IMAGE_TYPES}
            primaryActionLabel="Select File"
            onDrop={async files => {
              const file = files[0]

              const uploadResponse = await uploadTitleImage(file)

              if (!uploadResponse) return

              props.moduleForm.setFieldValue('titleImage', uploadResponse.fileName)
            }}
            description="This image will be displayed when the module is first introduced."
            hint="Drag and drop image file here or click below to browse. File should be in .jpg, .png, or .gif formats."
          />
        )}
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6 }}>
        <AIInputWrapper
          disabled={!hasSteps}
          fieldState={getFieldState('introduction')}
          onAskSidekick={() => generateFieldByName('cm', 'introduction')}
          onAccept={() => acceptCMSuggestion('introduction')}
          onReject={() => rejectSuggestion('introduction')}
          onRegenerate={() => regenerateFieldByName('cm', 'introduction')}>
          <Textarea
            ref={fieldRefs.introduction}
            label={getFieldDisplayName('introduction')}
            autosize
            withAsterisk
            disabled={!hasSteps}
            placeholder={!hasSteps ? 'Please add content above before editing this field' : undefined}
            resize="vertical"
            minRows={8}
            {...props.moduleForm.getInputProps('introduction')}
            {...getRequiredFieldDescriptionProps(
              props.moduleForm,
              'introduction',
              'The Sidekick will use this to introduce the module.',
            )}
            classNames={{ input: theme.cx(classes.mskIntroductionInput) }}
          />
        </AIInputWrapper>
      </Grid.Col>

      {/* <Grid.Col span={6}>
        <Stack gap="xxxl">
          <DatePickerInput
            clearable
            disabled
            label={<ComingSoon prefix="Expiration Date " />}
            leftSection={<IconCalendar size={24} stroke={1.5} />}
            leftSectionPointerEvents="none"
            {...props.moduleForm.getInputProps('expirationDate')}
            {...getRequiredFieldDescriptionProps(
              props.moduleForm,
              'expirationDate',
              'Module will be auto-archived on this date.',
            )}
          />

          <DatePickerInput
            clearable
            disabled
            label={<ComingSoon prefix="Due Date " />}
            leftSection={<IconCalendar size={24} stroke={1.5} />}
            leftSectionPointerEvents="none"
            {...props.moduleForm.getInputProps('dueDate')}
            {...getRequiredFieldDescriptionProps(
              props.moduleForm,
              'dueDate',
              'Deadline for users to complete the module.',
            )}
          />
        </Stack>
      </Grid.Col> */}

      {/* <Grid.Col span={6}>
        <InputLabel
          label={<ComingSoon prefix="Which segment of your population is this content for? " />}
          withDivider
          mb={theme.rem(12)}
        />

        <Stack gap="xl">
          <Checkbox.Group label="Age Groups">
            <Grid gutter="sm">
              <Grid.Col span={3}>
                <Checkbox label="18-24" value="18-24" />
              </Grid.Col>

              <Grid.Col span={3}>
                <Checkbox label="25-35" value="25-35" />
              </Grid.Col>

              <Grid.Col span={3}>
                <Checkbox label="36-50" value="36-50" />
              </Grid.Col>

              <Grid.Col span={3}>
                <Checkbox label="51+" value="51+" />
              </Grid.Col>
            </Grid>
          </Checkbox.Group>

          <Radio.Group label="Gender">
            <Grid gutter="sm">
              <Grid.Col span={3}>
                <Radio label="Men" value="Men" />
              </Grid.Col>

              <Grid.Col span={3}>
                <Radio label="Women" value="Women" />
              </Grid.Col>

              <Grid.Col span={3}>
                <Radio label="All" value="All" />
              </Grid.Col>
            </Grid>
          </Radio.Group>
        </Stack>
      </Grid.Col> */}

      {/* <Grid.Col span={6}>
        <InputLabel
          label={
            <ComingSoon prefix="Which media can the Expert Sidekick use in personalizing content delivery to member preferences? " />
          }
          withDivider
          mb={theme.rem(12)}
        />

        <Checkbox.Group>
          <Stack gap="lg">
            <Checkbox label="Audio" value="Audio" />
            <Checkbox label="Video" value="Video" />
            <Checkbox label="Text" value="Text" />
          </Stack>
        </Checkbox.Group>
      </Grid.Col> */}
    </Grid>
  )
})
